import {
	FormGroup,
	FormControl,
	AbstractControl,
	ValidationErrors,
	FormControlName,
} from '@angular/forms';
import { coord } from '../models/coord';
import { ElementRef, QueryList } from '@angular/core';
import {
	americanAndCanadianStateCodes,
	northAmericanStateCodes,
	AMERICANANDCANADIANSTATE_MAP_ABBREVIATION,
} from '../constants';
import { combineLatest, of } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { xor } from 'lodash';
import * as moment from 'moment';
import * as _ from 'lodash';
import {
	Address,
	Contact,
	InvoicedJobSummary,
	BillableJobSummary,
	InvoiceWithCutoffRequest,
	OfferedService,
	FieldWorkerLink,
	FieldWorker,
	FieldWorkerPayrollType,
	FieldWorkerLinkPayrollType,
} from '../api';
import { HttpResponse } from '@angular/common/http';
import { ParamMap } from '@angular/router';
import { CustomFormAccessor } from '../form-models/form-cva.component';
import { TouchableControl } from '../models/TouchableControl';

export function paramMapsAreEqual(p1: ParamMap, p2: ParamMap): boolean {
	const k1 = p1.keys;
	const k2 = p2.keys;

	return xor(k1, k2).length === 0;
}

export function getRequiredFields(form: FormGroup): string[] {
	return Object.keys(form.controls).filter(
		(key) =>
			form.get(key).validator &&
			form.get(key).validator(new FormControl()).required
	);
}

export function drawShape(el: any, cords: coord[], color: string) {
	const ctx = el.getContext('2d');
	const first = cords.shift();
	ctx.fillStyle = color;
	ctx.beginPath();
	ctx.moveTo(first.x, first.y);
	cords.forEach((c) => ctx.lineTo(c.x, c.y));
	ctx.closePath();
	ctx.fill();
}

export function adjustCalItemsSize(calItems: ElementRef) {
	const div = calItems.nativeElement;
	const hasVerticalScrollbar = div.scrollHeight > div.clientHeight;
	if (hasVerticalScrollbar) {
		div.style.width = 'calc(100% + 18px)';
	} else {
		div.style.width = '100%';
	}
}

export function isNumeric(value: any) {
	return !isEmptyInputValue(value) && !isNaN(value);
}

export function buildImage(base64: string): string {
	if (base64) {
		if (base64.startsWith('data:image')) {
			return base64;
		} else {
			return `data:image/jpeg;base64, ${base64}`;
		}
	} else {
		return '';
	}
}

// The following function is pulled direct from the Angular source code
// It's primarily used inside of input Validator functions
export function isEmptyInputValue(value: any): boolean {
	// we don't check for string here so it also works with arrays
	return value == null || value.length === 0;
}

export function isValidAddress(address: Address): boolean {
	if (
		!address ||
		!address.address1 ||
		!address.city ||
		!isValidUnitedOrCanadianState(address.state) ||
		!address.zipCode ||
		address.zipCode.length < 5
	) {
		return false;
	} else {
		return true;
	}
}

export function isValidUnitedOrCanadianState(state: string): boolean {
	if (state) {
		return americanAndCanadianStateCodes.includes(state.toUpperCase())
			? true
			: false;
	}
	return true;
}

export function isValidNorthAmericanState(state: string): boolean {
	if (state) {
		return northAmericanStateCodes.includes(state.toUpperCase())
			? true
			: false;
	}
	return true;
}

export function isIdentifiableObject(
	input: object | string
): input is { id: number } {
	return input && (input as { id: number }).id !== undefined;
}

export function isString(input: object | string): input is string {
	return typeof input === 'string';
}

export function idComparer(
	input1: { id?: number },
	input2: { id?: number }
): boolean {
	if (!input1 || !input2) {
		return false;
	}

	return input1.id === input2.id;
}

export function hasValidEmail(contacts: Contact[]) {
	const validEmail =
		contacts &&
		contacts.some(
			(c) =>
				c.emailAddresses &&
				c.emailAddresses.some(
					(e) =>
						e.email && e.email.includes('@') && e.email.length > 3
				)
		);
	return validEmail;
}

export function filterNorthAmericanStates(control: AbstractControl) {
	return combineLatest([
		of(northAmericanStateCodes),
		control.valueChanges.pipe(
			startWith(''),
			map((s) => (s ? s.toLowerCase() : ''))
		),
	]).pipe(
		map(([states, s]) =>
			states
				.filter((state) => state.toLowerCase().includes(s))
				.sort(
					(s1, s2) =>
						Number(s2.toLowerCase().startsWith(s)) -
						Number(s1.toLowerCase().startsWith(s))
				)
		)
	);
}

// This function is used in conjunction with a form control to filter
// all 50 states of the US, as in a drop-down.
export function filterStates(control: AbstractControl) {
	return combineLatest([
		of(americanAndCanadianStateCodes),
		control.valueChanges.pipe(
			startWith(''),
			map((s) => (s ? s.toLowerCase() : ''))
		),
	]).pipe(
		map(([states, s]) =>
			states
				.filter((state) => state.toLowerCase().includes(s))
				.sort(
					(s1, s2) =>
						Number(s2.toLowerCase().startsWith(s)) -
						Number(s1.toLowerCase().startsWith(s))
				)
		)
	);
}

export function filterMapStates(control: AbstractControl) {
	return combineLatest([
		of(AMERICANANDCANADIANSTATE_MAP_ABBREVIATION),
		control.valueChanges.pipe(
			startWith(''),
			map((s) => (s ? s.toLowerCase() : ''))
		),
	]).pipe(
		map(([states, s]) =>
			states
				.filter((state) => state.code.toLowerCase().includes(s))
				.sort(
					(s1, s2) =>
						Number(s2.code.toLowerCase().startsWith(s)) -
						Number(s1.code.toLowerCase().startsWith(s))
				)
		)
	);
}

// There's currently no way to get all of the errors from a formgroup.
// This will get the errors of the formgroup as well as each individual
// error of each formcontrol of the group
export function collectErrors(ctrl: {
	errors: ValidationErrors | null;
	controls?: {
		[key: string]: AbstractControl;
	};
}): ValidationErrors {
	if (isFormGroup(ctrl)) {
		return Object.keys(ctrl.controls).reduce(
			(a, k) =>
				ctrl.controls[k].errors ? a.concat(ctrl.controls[k].errors) : a,
			ctrl.errors ? [ctrl.errors] : []
		) as ValidationErrors;
	} else {
		return ctrl.errors;
	}
}

export function buildArrayFromEnum(enumType: {
	[id: number]: string;
}): string[] {
	return Object.keys(enumType).filter(
		(k) => typeof enumType[k as any] !== 'number'
	);
}

export function collectTouchableChildControls<T extends object = {}>(
	controls: QueryList<FormControlName>
): { onTouched: () => void }[] {
	return controls
		.map((c) => c.valueAccessor as CustomFormAccessor<T>)
		.filter(Boolean)
		.flatMap((c) =>
			c.childControls ? collectTouchableChildControls(c.childControls) : c
		)
		.filter((c) => c.onTouched);
}

export const touchControl =
	(control: { markAllAsTouched(): void }) => (fn: () => void) => () => {
		fn();
		control.markAllAsTouched();
	};

/**
 * This is a wrapper function so the form controls being
 * passed around can only have `markAsTouched` called on them,
 * not `setValue` or `patchValue`.
 * */
export function wrapChildControls<T extends object = {}>(
	controls: QueryList<FormControlName>
): TouchableControl[] {
	if (controls) {
		return controls
			.map((c) => c.valueAccessor as CustomFormAccessor<T>)
			.filter(Boolean)
			.map((c) => c.form as TouchableControl)
			.filter(Boolean);
	} else {
		return [];
	}
}

export function touchAllControls(childControls: QueryList<FormControlName>) {
	const controls = collectTouchableChildControls(childControls);
	controls.forEach((c) => c.onTouched());
}

export function isFormGroup(
	control: Partial<AbstractControl>
): control is FormGroup {
	return (<FormGroup>control).controls !== undefined;
}

export function isInvoicedJob(
	job: InvoicedJobSummary | BillableJobSummary
): job is InvoicedJobSummary {
	return (<InvoicedJobSummary>job).isBundled !== undefined;
}

export function isInvoiceWithCutoffRequest(
	invoiceCandidateRequest: InvoiceWithCutoffRequest | number
): invoiceCandidateRequest is InvoiceWithCutoffRequest {
	return (
		(<InvoiceWithCutoffRequest>invoiceCandidateRequest).jobId !== undefined
	);
}

export function parseDateParam(paramValue: string): moment.Moment {
	let date: moment.Moment;
	if (_.isNil(paramValue)) {
		return null;
	}

	date = /^\d{2}-\d{2}-\d{4}/.test(paramValue)
		? (date = moment.utc(paramValue, 'MM-DD-YYYY'))
		: /^\d{2}\/\d{2}\/\d{4}/.test(paramValue)
		? (date = moment.utc(paramValue, 'L'))
		: // Like native date parser, assume ISO UTC when year first.
		/^\d{4}-\d{2}-\d{2}/.test(paramValue)
		? (date = moment.utc(paramValue, 'YYYY-MM-DD'))
		: /^\d{9}$/.test(paramValue)
		? moment.unix(+paramValue).utc()
		: null;

	if (date && date.isValid()) {
		return date;
	}

	throw new Error(`Invalid date: ${paramValue}`);
}

export function displayName(obj: { name: string } | string) {
	return obj && typeof obj === 'object' && obj.name ? obj.name : obj;
}

export function displayDescription(obj: { description: string } | string) {
	return obj && typeof obj === 'object' && obj.description
		? obj.description
		: obj;
}

export function displayCode(obj: { code: string } | string) {
	return obj && typeof obj === 'object' && obj.code ? obj.code : obj;
}

export const bytesToSize = (bytes: number): string => {
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
	if (bytes === 0) {
		return '0 Byte';
	}
	const i = Math.floor(Math.log(bytes) / Math.log(1024));
	const num = bytes / Math.pow(1024, i);
	return Math.floor(num * 100) / 100 + ' ' + sizes[i];
};

export const bufferToBase64 = (buf: Uint8Array | ArrayBuffer): string => {
	buf = new Uint8Array(buf);
	const binstr = Array.prototype.map
		.call(buf, function (ch: number) {
			return String.fromCharCode(ch);
		})
		.join('');
	return btoa(binstr);
};

export const base64ToArrayBuffer = (base64: string) => {
	const binaryString = window.atob(base64);
	const binaryLen = binaryString.length;
	const bytes = new Uint8Array(binaryLen);
	for (let i = 0; i < binaryLen; i++) {
		const ascii = binaryString.charCodeAt(i);
		bytes[i] = ascii;
	}
	return bytes;
};

export function getFilenameFromHttpResponse(httpResponse: HttpResponse<Blob>) {
	const contentDispositionHeader = httpResponse.headers.get(
		'Content-Disposition'
	);
	const result = contentDispositionHeader.split(';')[1].trim().split('=')[1];
	return result.replace(/"/g, '');
}

export function grabIdFromParams(paramMap: ParamMap): number {
	const ID_KEY = 'id';

	return +paramMap.get(ID_KEY) || 0;
}

export function grabGuidFromParams(paramMap: ParamMap): string {
	const ID_KEY = 'id';

	return paramMap.get(ID_KEY) || '';
}

/** Creates a function that converts specified string fields into moments (utc)  */
export function momentBuilderFactory<T, K>(
	...props: (keyof T)[]
): (listArgs: T) => K {
	return (listArgs: T) => ({
		...listArgs,
		...props.reduce(
			(a, key) =>
				listArgs[key] ? { ...a, [key]: moment.utc(listArgs[key]) } : a,
			{} as K
		),
	});
}

/** Checks to see if an object has any truthy properties */
export function hasAnyNonZeroValues(obj: any): boolean {
	return Object.values<boolean>(obj).reduce((a, c) => !!a || !!c, false);
}

export const offeredServicesToAllowTbdJobDates = (
	offeredServices: OfferedService[]
) =>
	offeredServices.length &&
	offeredServices.some((s) => s.type.allowTbdJobDates);

export const displayImageNewTab = (base64Img: string) => {
	var image = new Image();
	image.src = base64Img;
	var newWindow = window.open('');
	newWindow?.document.write(image.outerHTML);
};

export const fieldWorkerToLink = (rwic: FieldWorker): FieldWorkerLink => {
	const link: FieldWorkerLink = {
		firstName: rwic.firstName,
		lastName: rwic.lastName,
		id: rwic.id,
		employeeCode: rwic.employeeCode,
		paycomId: rwic.paycomId,
		jobTitle: rwic.jobTitle,
		payrollType: rwic.payrollType as unknown as FieldWorkerLinkPayrollType,
	};

	return link;
};

export const fieldWorkerLinkToFieldWorker = (
	link: FieldWorkerLink
): FieldWorker => {
	const rwic: FieldWorker = {
		firstName: link.firstName,
		lastName: link.lastName,
		id: link.id,
		employeeCode: link.employeeCode,
		paycomId: link.paycomId,
		jobTitle: link.jobTitle,
		payrollType: link.payrollType as unknown as FieldWorkerPayrollType,
	};
	return rwic;
};
